import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Banner,
  Button,
  ckConfirm,
  DataTable,
  Dialog,
  exportRows,
  Field,
  QuickFilters,
  Segmented,
  Select,
  Tag,
  TextArea,
  TextInput,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import type { CkColSet, CkDataColumn, CkMenuItem, CkSelection, CkTableView } from "@NP-Webber/crm-kit/dist/next/index.js";
import { salesApi, type LeadDto, type LeadsPage as LeadsData, type LeadStage } from "../../api/salesApi";
import { ScreenHeader } from "../page/ScreenHeader";
import { useShell } from "../shell/ShellContext";
import { useServerTable, type EnumColumn } from "../useServerTable";
import { LeadSheet } from "./LeadSheet";
import { addDaysIso, copyText, errorMessage, fmtNum, fmtSecs, REGION_LABEL, STAGE_META, todayIso, withEmpty } from "./salesUi";
import { useSalesMeta } from "./useSalesMeta";
import { useYemotCall, YemotSendDialog, type YemotSendKindUi } from "./YemotOutbound";
import "./sales.css";

/** the quick cards — call today / overdue / this week / listened on the line ("due" = today + overdue, from the dashboard link) */
type Quick = "" | "today" | "overdue" | "week" | "due" | "listened";
const QUICK_LABEL: Record<Exclude<Quick, "">, string> = {
  today: "להתקשר היום",
  overdue: "באיחור",
  week: "השבוע",
  due: "היום ובאיחור",
  listened: "הקשיבו בקו",
};
const isQuick = (v: string | null): v is Quick => v === "" || (v !== null && v in QUICK_LABEL);

/** older links / the dashboard may still carry these named filters — kept as extra params with a chip */
const LINK_KEYS = ["statusKey", "outcomeKey", "repId", "productId", "region", "from", "to"] as const;

const TABLE_KEY = "tb.sales.leads";
const STAGES: LeadStage[] = ["new", "in_progress", "won", "lost", "done"];
const NO_IDS: string[] = [];

/** «ליד חדש» */
function NewLeadDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const toast = useCkToast();
  const { optionsOf, productOptions, repOptions } = useSalesMeta();
  const blank = { name: "", phone: "", source: "", interestProductId: "", statusKey: "", assignedRepId: "", notes: "", followUpOn: "" };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF({ ...blank, followUpOn: todayIso() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const set = (k: keyof typeof blank, v: string) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.name.trim() && !f.phone.trim()) {
      toast.error("צריך שם או טלפון");
      return;
    }
    setBusy(true);
    try {
      const l = await salesApi.leads.create({
        name: f.name.trim() || null,
        phone: f.phone.trim() || null,
        source: f.source || null,
        interestProductId: f.interestProductId || null,
        statusKey: f.statusKey || null,
        assignedRepId: f.assignedRepId || null,
        notes: f.notes.trim() || null,
        followUpOn: f.followUpOn || null,
        inquiryOn: todayIso(),
      });
      toast.success("הליד נוסף");
      onClose();
      onCreated(l.id);
    } catch (e) {
      toast.error(errorMessage(e, "ההוספה נכשלה"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="md"
      icon="phone"
      title="ליד חדש"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            {busy ? "שומר…" : "הוספה"}
          </Button>
        </>
      }
    >
      <div className="sl-grid2">
        <Field label="שם">
          <TextInput value={f.name} onChange={(e) => set("name", e.target.value)} autoFocus />
        </Field>
        <Field label="טלפון">
          <TextInput ltr type="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
        </Field>
        <Field label="מקור">
          <Select options={withEmpty("—", optionsOf("sales_lead_source"))} value={f.source} onChange={(v) => set("source", v)} aria-label="מקור" />
        </Field>
        <Field label="מתעניין ב־">
          <Select options={withEmpty("—", productOptions)} value={f.interestProductId} onChange={(v) => set("interestProductId", v)} aria-label="מוצר" />
        </Field>
        <Field label="סטטוס">
          <Select options={withEmpty("—", optionsOf("sales_lead_status"))} value={f.statusKey} onChange={(v) => set("statusKey", v)} aria-label="סטטוס" />
        </Field>
        <Field label="נציג">
          <Select options={withEmpty("— ללא —", repOptions)} value={f.assignedRepId} onChange={(v) => set("assignedRepId", v)} aria-label="נציג" />
        </Field>
        <Field label="לחזור ב־">
          <TextInput type="date" value={f.followUpOn} onChange={(e) => set("followUpOn", e.target.value)} />
        </Field>
      </div>
      <div style={{ marginTop: 12 }}>
        <Field label="הערות" full>
          <TextArea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/** «תיעוד שיחה» from the row — outcome, a note and the next call, without opening the card */
function LogCallDialog({ lead, onClose, onLogged }: { lead: LeadDto | null; onClose: () => void; onLogged: () => void }) {
  const toast = useCkToast();
  const { optionsOf } = useSalesMeta();
  const [outcome, setOutcome] = useState("");
  const [body, setBody] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setOutcome("");
    setBody("");
    setNext(lead?.followUpOn ?? "");
  }, [lead]);
  const today = todayIso();
  const quick = [
    { label: "מחר", value: addDaysIso(today, 1) },
    { label: "עוד 3 ימים", value: addDaysIso(today, 3) },
    { label: "שבוע", value: addDaysIso(today, 7) },
    { label: "בלי", value: "" },
  ];
  const submit = async () => {
    if (!lead) return;
    if (!outcome && !body.trim()) {
      toast.error("בחרו תוצאה או כתבו מה היה בשיחה");
      return;
    }
    setBusy(true);
    try {
      await salesApi.leads.log(lead.id, { kind: "call", outcomeKey: outcome || null, body: body.trim() || null, followUpOn: next || null });
      toast.success("השיחה תועדה");
      onClose();
      onLogged();
    } catch (e) {
      toast.error(errorMessage(e, "התיעוד נכשל"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={Boolean(lead)}
      onClose={onClose}
      size="sm"
      icon="phone"
      title={`תיעוד שיחה${lead?.name ? ` — ${lead.name}` : ""}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button variant="primary" icon="check" disabled={busy} onClick={() => void submit()}>
            {busy ? "שומר…" : "תיעוד השיחה"}
          </Button>
        </>
      }
    >
      <Field label="תוצאה">
        <Select options={withEmpty("— בחירה —", optionsOf("sales_lead_outcome"))} value={outcome} onChange={setOutcome} aria-label="תוצאת השיחה" />
      </Field>
      <Field label="מה היה בשיחה" full>
        <TextArea rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
      </Field>
      <Field label="לחזור ב־">
        <div className="sl-row">
          <TextInput type="date" value={next} onChange={(e) => setNext(e.target.value)} style={{ width: 160 }} />
          {quick.map((q) => (
            <Button key={q.label} size="sm" variant={next === q.value ? "primary" : "default"} onClick={() => setNext(q.value)}>
              {q.label}
            </Button>
          ))}
        </div>
      </Field>
    </Dialog>
  );
}

/** bulk: assign a rep / move to a stage (POST /sales/leads/bulk, 500 per request) */
function BulkLeadsDialog({
  kind,
  ids,
  onClose,
  onDone,
}: {
  kind: "rep" | "stage" | null;
  ids: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useCkToast();
  const { repOptions } = useSalesMeta();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setValue(""), [kind]);
  const submit = async () => {
    if (kind === "stage" && !value) {
      toast.error("בחרו שלב");
      return;
    }
    setBusy(true);
    try {
      let updated = 0;
      for (let i = 0; i < ids.length; i += 500) {
        const chunk = ids.slice(i, i + 500);
        const r = await salesApi.leads.bulk(kind === "rep" ? { ids: chunk, assignedRepId: value || null } : { ids: chunk, stage: value as LeadStage });
        updated += r.updated;
      }
      toast.success(`${fmtNum(updated)} לידים עודכנו`);
      onClose();
      onDone();
    } catch (e) {
      toast.error(errorMessage(e, "העדכון נכשל"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={Boolean(kind)}
      onClose={onClose}
      size="sm"
      icon={kind === "rep" ? "user" : "flag"}
      title={`${kind === "rep" ? "שיוך לנציג" : "שינוי שלב"} — ${fmtNum(ids.length)} לידים`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button variant="primary" icon="check" disabled={busy} onClick={() => void submit()}>
            {busy ? "מעדכן…" : "עדכון"}
          </Button>
        </>
      }
    >
      {kind === "rep" ? (
        <Field label="נציג">
          <Select options={withEmpty("— ללא נציג —", repOptions)} value={value} onChange={setValue} aria-label="נציג" />
        </Field>
      ) : (
        <Field label="שלב">
          <Select options={withEmpty("— בחירה —", STAGES.map((s) => ({ value: s, label: STAGE_META[s].label })))} value={value} onChange={setValue} aria-label="שלב" />
        </Field>
      )}
    </Dialog>
  );
}

function initialState(p: URLSearchParams): { stage: string; quick: Quick; link: Record<string, string> } {
  const due = p.get("due") ?? "";
  const quick: Quick = p.get("listened") === "1" ? "listened" : isQuick(due) ? due : "";
  // arriving with a "due" filter: show every open lead that is due (stage is implied)
  const stage = p.has("stage") ? p.get("stage") ?? "" : due ? "" : "open";
  const link: Record<string, string> = {};
  LINK_KEYS.forEach((k) => {
    const v = p.get(k);
    if (v) link[k] = v;
  });
  return { stage, quick, link };
}

/**
 * /sales/leads — who to call (Turbo DataTable anatomy): stage tabs, quick cards, typed columns with column filters,
 * column sets, views, row actions, bulk actions over the selection or all results, export; a row opens the lead card.
 */
export function LeadsPage() {
  const { id } = useParams<{ id?: string }>();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useCkToast();
  const { allowed } = useShell();
  const canEdit = allowed("sales.edit");
  const canManage = allowed("sales.manage");
  const { label, optionsOf } = useSalesMeta();

  const [init] = useState(() => initialState(params));
  const [stage, setStage] = useState(init.stage);
  const [quick, setQuick] = useState<Quick>(init.quick);
  const [link, setLink] = useState<Record<string, string>>(init.link);
  const [creating, setCreating] = useState(false);
  const [logging, setLogging] = useState<LeadDto | null>(null);
  const [bulk, setBulk] = useState<{ kind: "rep" | "stage"; ids: string[] } | null>(null);
  const [yemotSend, setYemotSend] = useState<{ kind: YemotSendKindUi; ids: string[] } | null>(null);
  const [clearKey, setClearKey] = useState(0);

  const enums = useMemo<Record<string, EnumColumn>>(
    () => ({
      stage: { label: (v) => STAGE_META[v as LeadStage]?.label ?? v, options: STAGES.map((s) => ({ value: s, label: STAGE_META[s].label })) },
      statusKey: { label: (v) => label("sales_lead_status", v), options: optionsOf("sales_lead_status", { all: true }) },
      outcomeKey: { label: (v) => label("sales_lead_outcome", v), options: optionsOf("sales_lead_outcome", { all: true }) },
      source: { label: (v) => label("sales_lead_source", v), options: optionsOf("sales_lead_source", { all: true }) },
      region: { label: (v) => REGION_LABEL[v] ?? v, options: Object.entries(REGION_LABEL).map(([value, l]) => ({ value, label: l })) },
    }),
    [label, optionsOf],
  );

  const extra = useMemo(() => {
    const e: Record<string, string> = { ...link };
    if (stage) e.stage = stage;
    if (quick === "listened") e.listened = "1";
    else if (quick) e.due = quick;
    return e;
  }, [stage, quick, link]);

  const table = useServerTable<LeadDto, LeadsData>({
    load: (p) => salesApi.leads.list(p),
    loadValues: (p) => salesApi.leads.columnValues(p),
    extra,
    enums,
    errorText: "לא הצלחנו לטעון את הלידים",
  });
  const { query, setQuery, firstPage, data, reload } = table;
  const yemotCall = useYemotCall(reload);
  const yemotOn = Boolean(yemotCall.setup?.configured);

  // keep the screen's own filters in the URL (links / back button restore them)
  useEffect(() => {
    const next = new URLSearchParams();
    if (stage !== (quick && quick !== "listened" ? "" : "open")) next.set("stage", stage);
    if (quick === "listened") next.set("listened", "1");
    else if (quick) next.set("due", quick);
    Object.entries(link).forEach(([k, v]) => next.set(k, v));
    if (next.toString() !== params.toString() && params.get("new") !== "1") setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, quick, link]);

  useEffect(() => {
    if (params.get("new") === "1") {
      if (canEdit) setCreating(true);
      const next = new URLSearchParams(params);
      next.delete("new");
      setParams(next, { replace: true });
    }
  }, [params, setParams, canEdit]);

  const changeStage = (v: string) => {
    setStage(v);
    firstPage();
  };
  const changeQuick = (k: string) => {
    const q = (k === "all" ? "" : k) as Quick;
    setQuick(q);
    // the due cards are about open leads — from a closed tab, show every stage
    if (q && q !== "listened" && stage && stage !== "open" && stage !== "new" && stage !== "in_progress") setStage("");
    firstPage();
  };

  const qs = () => window.location.search || "";
  const openLead = (lid: string) => navigate(`/sales/leads/${lid}${qs()}`);
  const closeLead = () => navigate(`/sales/leads${qs()}`);
  const afterChange = () => {
    setClearKey((k) => k + 1);
    reload();
  };

  const today = todayIso();
  const columns = useMemo<Array<CkDataColumn<LeadDto>>>(
    () => [
      { key: "name", label: "שם", type: "text", sortable: true, render: (r) => <b>{r.name || "—"}</b> },
      { key: "phone", label: "טלפון", type: "text", ltr: true, copy: true, onCopied: () => toast("הועתק"), getValue: (r) => r.phone ?? "" },
      { key: "source", label: "מקור", type: "enum", sortable: true, getValue: (r) => label("sales_lead_source", r.source) },
      { key: "interest", label: "מתעניין ב", type: "text", sortable: true, getValue: (r) => r.interestProductName ?? r.interestText ?? "" },
      {
        key: "statusKey",
        label: "סטטוס",
        type: "enum",
        sortable: true,
        getValue: (r) => label("sales_lead_status", r.statusKey),
        render: (r) => (r.statusKey ? <Tag tone="neutral">{label("sales_lead_status", r.statusKey)}</Tag> : <span className="sl-muted">—</span>),
      },
      {
        key: "stage",
        label: "שלב",
        type: "enum",
        sortable: true,
        getValue: (r) => STAGE_META[r.stage]?.label ?? r.stage,
        render: (r) => <Tag tone={STAGE_META[r.stage]?.tone ?? "neutral"}>{STAGE_META[r.stage]?.label ?? r.stage}</Tag>,
      },
      {
        key: "outcomeKey",
        label: "תוצאה אחרונה",
        type: "enum",
        sortable: true,
        getValue: (r) => label("sales_lead_outcome", r.outcomeKey),
        render: (r) => (
          <span className="sl-cell2">
            <span>{label("sales_lead_outcome", r.outcomeKey) || "—"}</span>
            {r.noAnswerCount ? <small>לא ענה ×{r.noAnswerCount}</small> : null}
          </span>
        ),
      },
      {
        key: "followUpOn",
        label: "לחזור ב",
        type: "date",
        sortable: true,
        render: (r) => <span className={r.followUpOn && r.followUpOn < today && (r.stage === "new" || r.stage === "in_progress") ? "sl-danger" : undefined}>{r.followUpOn ? r.followUpOn.split("-").reverse().join("/") : "—"}</span>,
      },
      { key: "repName", label: "נציג", type: "enum", sortable: true, getValue: (r) => r.assignedRepName ?? "" },
      { key: "inquiryOn", label: "תאריך פנייה", type: "date", sortable: true },
      { key: "lastContactOn", label: "שיחה אחרונה", type: "date", sortable: true },
      { key: "noAnswerCount", label: "לא ענה", type: "number", sortable: true, sum: true },
      { key: "listenCount", label: "הקשבות", type: "number", sortable: true, sum: true, render: (r) => (r.listenCount ? `×${r.listenCount}` : "") },
      { key: "listenSeconds", label: "משך הקשבה", type: "number", sortable: true, render: (r) => (r.listenSeconds ? <span className="sl-num">{fmtSecs(r.listenSeconds)}</span> : "") },
      { key: "lastListenAt", label: "הקשבה אחרונה", type: "date", sortable: true },
      { key: "listenedOn", label: "תאריך הקשבה", type: "date", sortable: true },
      { key: "region", label: "אזור", type: "enum", sortable: true, getValue: (r) => REGION_LABEL[r.region] ?? r.region },
      { key: "notes", label: "הערות", type: "text", getValue: (r) => r.notes ?? "" },
      { key: "createdAt", label: "נוצר", type: "date", sortable: true },
    ],
    [label, today, toast],
  );

  const colSets: CkColSet[] = [
    { key: "basic", label: "בסיסי", cols: ["name", "phone", "statusKey", "outcomeKey", "followUpOn", "repName", "inquiryOn"] },
    { key: "contact", label: "קשר", cols: ["name", "phone", "source", "region", "interest", "notes"] },
    { key: "follow", label: "מעקב", cols: ["name", "stage", "statusKey", "outcomeKey", "noAnswerCount", "lastContactOn", "followUpOn", "repName"] },
    { key: "listen", label: "הקשבה", cols: ["name", "phone", "listenCount", "listenSeconds", "lastListenAt", "listenedOn", "source"] },
  ];
  const basic = colSets[0]!.cols;
  const hiddenByDefault = columns.map((c) => c.key).filter((k) => !basic.includes(k) && k !== "interest" && k !== "listenCount");

  const builtinViews: CkTableView[] = [
    { id: "open", label: "לידים פתוחים", state: { query: { search: "", filters: {}, sort: null }, extra: { stage: "open", quick: "" } } },
    { id: "due", label: "להתקשר — היום ובאיחור", state: { query: { search: "", filters: {}, sort: { key: "followUpOn", dir: 1 } }, extra: { stage: "", quick: "due" } } },
    { id: "listened", label: "הקשיבו לאחרונה", state: { query: { search: "", filters: {}, sort: { key: "lastListenAt", dir: -1 } }, extra: { stage: "open", quick: "listened" } } },
    { id: "norep", label: "ללא נציג", state: { query: { search: "", filters: { repName: { mode: "cond", op: "empty" } }, sort: null }, extra: { stage: "open", quick: "" } } },
    { id: "all", label: "כל הלידים", state: { query: { search: "", filters: {}, sort: { key: "inquiryOn", dir: -1 } }, extra: { stage: "", quick: "" } } },
  ];

  const c = data?.stageCounts;
  const n = (s: LeadStage) => c?.[s] ?? 0;
  const qc = data?.quickCounts;
  const quickItems = [
    { key: "all", label: "כל הלידים בלשונית", icon: "users", tone: "primary" as const, value: qc?.all },
    { key: "today", label: QUICK_LABEL.today, icon: "phone", tone: "info" as const, value: qc?.today },
    { key: "overdue", label: QUICK_LABEL.overdue, icon: "clock", tone: "danger" as const, value: qc?.overdue, warn: true },
    { key: "week", label: QUICK_LABEL.week, icon: "calendar", tone: "violet" as const, value: qc?.week },
    { key: "listened", label: QUICK_LABEL.listened, icon: "mic", tone: "success" as const, value: qc?.listened },
  ];
  const extraChips = [
    quick ? { key: "quick", label: QUICK_LABEL[quick], tone: "primary" as const, onRemove: () => changeQuick("all") } : null,
    Object.keys(link).length ? { key: "link", label: "סינון מקישור", icon: "link", tone: "neutral" as const, onRemove: () => setLink({}) } : null,
  ].filter((x): x is NonNullable<typeof x> => Boolean(x));

  const removeLead = async (r: LeadDto) => {
    const ok = await ckConfirm({ title: "מחיקת ליד", message: `למחוק את «${r.name || r.phone || "הליד"}»? יימחקו גם השיחות שתועדו.`, confirmLabel: "מחיקה", danger: true });
    if (!ok) return;
    try {
      await salesApi.leads.remove(r.id);
      toast.success("הליד נמחק");
      if (id === r.id) closeLead();
      reload();
    } catch (e) {
      toast.error(errorMessage(e, "המחיקה נכשלה"));
    }
  };

  const rowMenu = (r: LeadDto): CkMenuItem[] => {
    const items: CkMenuItem[] = [{ key: "open", label: "פתיחת כרטיס", icon: "expand", kbd: "Enter", onClick: () => openLead(r.id) }];
    if (canEdit) items.push({ key: "log", label: "תיעוד שיחה", icon: "phone", onClick: () => setLogging(r) });
    if (canEdit && yemotOn && r.phone) items.push({ key: "yemot-call", label: "חיוג דרך ימות", icon: "phone", onClick: () => yemotCall.start(r) });
    if (canManage && yemotOn && r.phone) items.push({ key: "yemot-tzintuk", label: "צינתוק", icon: "bell", onClick: () => setYemotSend({ kind: "tzintuk", ids: [r.id] }) });
    if (r.phone) {
      items.push({
        key: "copy",
        label: "העתקת טלפון",
        icon: "copy",
        onClick: () => void copyText(r.phone ?? "").then((ok) => (ok ? toast("הועתק") : toast.error("ההעתקה נחסמה בדפדפן"))),
      });
    }
    if (canManage) items.push({ divider: true }, { key: "delete", label: "מחיקת ליד", icon: "trash", danger: true, onClick: () => void removeLead(r) });
    return items;
  };

  const bulkActions = [
    ...(canEdit
      ? [
          { key: "rep", label: "שיוך לנציג", icon: "user", onClick: async (sel: CkSelection) => setBulk({ kind: "rep", ids: await table.resolveIds(sel) }) },
          { key: "stage", label: "שינוי שלב", icon: "flag", onClick: async (sel: CkSelection) => setBulk({ kind: "stage", ids: await table.resolveIds(sel) }) },
        ]
      : []),
    // Yemot sends (Turbo «שיגור»): every selected / filtered lead — fetched in full, deduped by phone on the server
    ...(canManage && yemotOn
      ? [
          { key: "yemot-tzintuk", label: "צינתוק", icon: "bell", onClick: async (sel: CkSelection) => setYemotSend({ kind: "tzintuk", ids: await table.resolveIds(sel) }) },
          { key: "yemot-voice", label: "הודעה קולית", icon: "mic", onClick: async (sel: CkSelection) => setYemotSend({ kind: "voice", ids: await table.resolveIds(sel) }) },
        ]
      : []),
  ];

  return (
    <div className="sl-page">
      <ScreenHeader
        sub="מי להתקשר אליו היום, מה היה בשיחה האחרונה ומי הקשיב בקו"
        primary={canEdit ? { label: "ליד חדש", icon: "plus", onClick: () => setCreating(true) } : undefined}
      />
      <div className="sl-tabs">
        <Segmented
          ariaLabel="שלב"
          value={stage}
          onChange={changeStage}
          items={[
            { value: "open", label: "פתוחים", count: c ? n("new") + n("in_progress") : undefined },
            { value: "new", label: "חדשים", count: c ? n("new") : undefined },
            { value: "in_progress", label: "בטיפול", count: c ? n("in_progress") : undefined },
            { value: "won", label: "נסגרו בהצלחה", count: c ? n("won") : undefined },
            { value: "lost", label: "לא רלוונטי", count: c ? n("lost") : undefined },
            { value: "done", label: "טופל", count: c ? n("done") : undefined },
            { value: "", label: "הכל", count: c ? Object.values(c).reduce((a, b) => a + b, 0) : undefined },
          ]}
        />
      </div>
      <QuickFilters items={quickItems} value={quick || "all"} onChange={changeQuick} loading={table.loading && !data} />
      {table.error && data ? <Banner tone="danger">{table.error}</Banner> : null}
      <DataTable<LeadDto>
        mode="server"
        columns={columns}
        rows={table.rows}
        total={table.total}
        loading={table.loading}
        error={data ? null : table.error}
        onRetry={reload}
        onRefresh={reload}
        query={query}
        onQueryChange={setQuery}
        storageKey={TABLE_KEY}
        layoutDefaults={{ hidden: hiddenByDefault, pinned: ["name"] }}
        colSets={colSets}
        getValueOptions={table.getValueOptions}
        searchPlaceholder="חיפוש שם, טלפון, מקור, הערות…"
        extraChips={extraChips}
        onClearAll={() => {
          changeQuick("all");
          setLink({});
        }}
        builtinViews={builtinViews}
        getExtraViewState={() => ({ stage, quick })}
        onApplyView={(x) => {
          const v = (x ?? {}) as { stage?: string; quick?: string };
          setStage(v.stage ?? "open");
          setQuick(isQuick(v.quick ?? "") ? ((v.quick ?? "") as Quick) : "");
        }}
        activeRowId={id ?? null}
        onRowClick={(r) => openLead(r.id)}
        rowActions={(r) => [
          ...(canEdit ? [{ key: "log", icon: "phone", label: "תיעוד שיחה", onClick: () => setLogging(r) }] : []),
          { key: "open", icon: "expand", label: "פתיחת כרטיס", onClick: () => openLead(r.id) },
        ]}
        rowMenu={rowMenu}
        bulkActions={bulkActions}
        clearSelectionKey={clearKey}
        exportFilename={`לידים-${today}`}
        onExport={async ({ format, columns: cols, selection }) => {
          const list = await table.resolveRows(selection);
          await exportRows(list, cols, { format, filename: `לידים-${today}` });
          toast.success(`יוצאו ${fmtNum(list.length)} שורות`);
        }}
        exportNote="כשיש בחירה — רק הנבחרים; אחרת כל התוצאות של הסינון."
        emptyTitle="אין לידים שמתאימים לסינון"
        emptyText={stage === "open" && !quick ? "אין לידים פתוחים. «ליד חדש» למעלה, או לשונית «הכל»." : "נסו לנקות את הסינון או לבחור לשונית אחרת."}
        actionsWidth={canEdit ? 112 : 84}
        renderMobileRow={(r) => (
          <>
            <b>{r.name || r.phone || "—"}</b>
            <small>
              {[STAGE_META[r.stage]?.label, r.followUpOn ? `לחזור ${r.followUpOn.split("-").reverse().join("/")}` : null, r.assignedRepName].filter(Boolean).join(" · ")}
            </small>
          </>
        )}
      />

      {id ? <LeadSheet key={id} id={id} onClose={closeLead} onChanged={reload} /> : null}
      <NewLeadDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(lid) => {
          reload();
          openLead(lid);
        }}
      />
      <LogCallDialog lead={logging} onClose={() => setLogging(null)} onLogged={reload} />
      <BulkLeadsDialog kind={bulk?.kind ?? null} ids={bulk?.ids ?? []} onClose={() => setBulk(null)} onDone={afterChange} />
      <YemotSendDialog kind={yemotSend?.kind ?? null} ids={yemotSend?.ids ?? NO_IDS} onClose={() => setYemotSend(null)} onDone={afterChange} />
      {yemotCall.dialog}
    </div>
  );
}

export default LeadsPage;
